// ABOUTME: Per-line warehouse-location UI for immediate return processing.
// ABOUTME: Shows the existing return location, or lets the user bind one, mirroring ReturnsToProcessView.
import { useTheme } from '@emotion/react';
import { Box, ButtonBase } from '@mui/material';
import cx from "classnames";
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetProductReturnLocationInfoQuery } from '../../../newapi/return/returnsSlice';
import { useGetWarehouseLocationSelectQuery } from '../../../newapi/warehouse/warehouselocationSlice';
import { tokens } from '../../../theme';
import { getWarehouseLocationLabelData } from '../../../utils/labelColorText';
import { warehouselocationtype_type } from '../../../utils/staticEnums';
import { ShDropdownSearchQuery } from '../../theme/dropdowns/Dropdown';
import ShLabel from '../../theme/label/ShLabel';
import Text from '../../theme/text/Text';

const ReturnLineLocation = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);

    const { data: locationInfo, isLoading, isSuccess } = useGetProductReturnLocationInfoQuery(props.productId, {
        skip: !props.productId,
    });


    // Surface hasReturnLocation onto the line so the wrapper can require a bound
    // location before saving.
    useEffect(() => {
        if (isSuccess && locationInfo && props.value?.hasReturnLocation !== locationInfo.hasReturnLocation) {
            props.onChange({ hasReturnLocation: locationInfo.hasReturnLocation });
        }
    }, [isSuccess, locationInfo?.hasReturnLocation]);

    if (isLoading || !locationInfo) {
        return null;
    }

    const rowLabels = (location) => {
        const labelData = getWarehouseLocationLabelData(location.locationType, theme);
        return (
            <>
                <ShLabel borderless size={24} fitted variant={theme.palette.mode === "dark" ? "greyLight" : "grey"} palette="normal">
                    {location.stock + " "}
                    <Text light>{t("available")}</Text>
                </ShLabel>
                <ShLabel borderless size={24} fitted variant={labelData.labelcolor} palette="normal">
                    {t(labelData.labeltext)}
                </ShLabel>
            </>
        );
    };

    // STATE A: the product already has a home; show it, no choice needed.
    if (locationInfo.hasReturnLocation) {
        return (
            <Box sx={{ mt:2, p:1.5, borderRadius:3, background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100] }}>
                <Text bold>{t("locations")}</Text>
                <Box sx={{ mt:1, mb:1, display:"flex", flexDirection:"column", gap:1 }}>
                    {locationInfo.existingLocations?.map((warehouse) =>
                        warehouse.locations?.map((location) => {
                            if (location.locationType !== warehouselocationtype_type.GENERAL) return null;
                            return (
                                <Box
                                    key={"exloc" + location.id}
                                    sx={{
                                        width:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                        display:"flex",
                                        height:32,
                                        alignItems:'center',
                                        pl:1.5,
                                        pr:0.5,
                                        borderRadius:4,
                                        justifyContent:'space-between',
                                        border:`1px solid ${colors.grey[100]}`,
                                        boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                    }}
                                >
                                    <Text>{location.location}</Text>
                                    {rowLabels(location)}
                                </Box>
                            );
                        })
                    )}
                </Box>
            </Box>
        );
    }

    // STATE B: no home yet; the user must bind a location.
    return (
        <Box sx={{ mt:2, p:1.5, borderRadius:3, background: colors.blue[200] }}>
            <Text bold>{t("choose_a_location_to_move_to")}</Text>
            <Box sx={{ mt:1, mb:1, display:"flex", flexDirection:"column" }}>
                {locationInfo.availableLocations?.map((warehouse, widx) => (
                    <Box key={"wh" + widx} sx={{ mb:3, display:"flex", flexDirection:'column', gap:1 }}>
                        {warehouse.locations?.map((location) => {
                            if (location.locationType !== warehouselocationtype_type.GENERAL) return null;
                            return (
                                <ButtonBase
                                    key={"avloc" + location.id}
                                    onClick={() => props.onChange({
                                        selectedWarehouseLocationIndex: -1,
                                        warehouseLocationId: location.id,
                                        chosenLocation: { id: location.id, name: location.location },
                                    })}
                                    className={cx(undefined, { "selected": props.value?.warehouseLocationId === location.id })}
                                    sx={{
                                        width:1,
                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                        display:"flex",
                                        height:32,
                                        alignItems:'center',
                                        pl:1.5,
                                        pr:0.5,
                                        borderRadius:4,
                                        justifyContent:'space-between',
                                        border:`1px solid ${colors.grey[100]}`,
                                        boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                        "&.selected": {
                                            background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                            border:`1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                        }
                                    }}
                                >
                                    <Text>{location.location}</Text>
                                    {rowLabels(location)}
                                </ButtonBase>
                            );
                        })}
                    </Box>
                ))}
                <ShDropdownSearchQuery
                    name="name"
                    onChange={(selected, option) => {
                        if (selected === -1) return;
                        props.onChange({
                            selectedWarehouseLocationIndex: selected,
                            warehouseLocationId: option.id,
                            chosenLocation: option,
                        });
                    }}
                    displayName={"name"}
                    content={useGetWarehouseLocationSelectQuery}
                    closeOnSelection
                    multiple={false}
                    selected={props.value?.selectedWarehouseLocationIndex ?? -1}
                    searchOptions={{ placeholder: t("search") }}
                >
                    {({ anchorEl, onClick }) => (
                        <ButtonBase
                            onClick={onClick}
                            sx={{
                                width:1,
                                height:32,
                                borderRadius:5,
                                background:theme.palette.mode === "dark" ? colors.blue[400] : colors.blue[400],
                                display:"flex",
                                alignItems:'center',
                                px:0.5,
                                position:'relative',
                            }}
                        >
                            <Text bold color={theme.palette.mode === "dark" && colors.txt["primaryDark"]}>
                                {props.value?.chosenLocation ? props.value.chosenLocation.name : t("nothing_selected")}
                            </Text>
                        </ButtonBase>
                    )}
                </ShDropdownSearchQuery>
                {props.fieldError?.warehouseLocationId &&
                    <Text variant="red" sx={{ mt:1 }}>{t("choose_a_location_to_book_the_items_onto")}</Text>
                }
            </Box>
        </Box>
    );
};

export default ReturnLineLocation;
